/**
 * @file KnowledgeBaseMention 与 textarea 衔接：`@` 唤起、query 同步、cleanup
 * 触发态机委托 mentionShared/useMentionTrigger，本文件只做命名适配。
 */
import { useMentionTrigger, type TInputSurface } from '../../mentionShared/useMentionTrigger';

/** 知识库 mention 打开原因：按钮点击 / @ 输入触发 */
export type TKnowledgeMentionOpenReason = 'button' | 'at';

/**
 * useKnowledgeMentionHost 选项
 * @interface IUseKnowledgeMentionHostOptions
 */
export interface IUseKnowledgeMentionHostOptions {
  /** 输入框当前值 */
  inputValue: string;
  /** 设置输入框值 */
  setInputValue: (value: string) => void;
  /** textarea DOM 引用 */
  textareaRef: React.RefObject<TInputSurface | null>;
}

/**
 * 透传给 KnowledgeBaseMention 组件的 props
 * @interface IKnowledgeMentionHostProps
 */
export interface IKnowledgeMentionHostProps {
  /** 下拉面板是否打开 */
  knowledgeDropdownOpen: boolean;
  /** 下拉面板打开/关闭回调 */
  onKnowledgeDropdownOpenChange: (open: boolean) => void;
  /** 按钮 / hover 显式打开（切到 button 模式，清 @ 触发索引） */
  openKnowledgeDropdownAsButton: () => void;
  /** @ 输入后的查询文本 */
  knowledgeMentionQuery: string;
  /** 打开原因 */
  knowledgeMentionOpenReason: TKnowledgeMentionOpenReason;
  /** 虚拟锚点 DOM 引用 */
  knowledgeMentionAnchorRef: React.RefObject<HTMLElement | null>;
}

/**
 * useKnowledgeMentionHost 返回值
 * @interface IUseKnowledgeMentionHostResult
 */
export interface IUseKnowledgeMentionHostResult {
  /** 包装后的 textarea onChange 处理函数；返回 true 表示已处理，调用方应 return */
  wrapTextareaChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => boolean;
  /** 透传给 KnowledgeBaseMention 组件的 props */
  knowledgeMentionHostProps: IKnowledgeMentionHostProps;
  /** @ 模式是否已打开 */
  isAtModeOpen: boolean;
  /** 清理 @ 触发符+query，传入 insertLabel 则在原位置插入引用串 */
  cleanupAtText: (
    insertLabel?: string,
    insertOptions?: import('../../mentionEditor/types').IInsertMentionOptions,
  ) => void;
  /** textarea focus 时的重检处理 */
  handleTextareaFocus: () => void;
}

/**
 * 知识库 @ 唤起态机适配 hook
 * 委托 mentionShared/useMentionTrigger 实现触发符检测逻辑
 * @param options - hook 选项
 * @returns 与 KnowledgeBaseMention 衔接的 props 和事件处理函数
 */
export const useKnowledgeMentionHost = ({
  inputValue,
  setInputValue,
  textareaRef,
}: IUseKnowledgeMentionHostOptions): IUseKnowledgeMentionHostResult => {
  const trigger = useMentionTrigger({
    triggerChar: '@',
    charOpenReason: 'at',
    inputValue,
    setInputValue,
    textareaRef,
    anchorDataAttr: 'data-kb-mention-anchor',
    // 与 Skill 对齐：关窗清 index，避免第二次 @ 沿用旧触发位
    clearIndexOnClose: true,
  });

  return {
    wrapTextareaChange: trigger.wrapTextareaChange,
    knowledgeMentionHostProps: {
      knowledgeDropdownOpen: trigger.dropdownOpen,
      onKnowledgeDropdownOpenChange: trigger.onDropdownOpenChange,
      openKnowledgeDropdownAsButton: trigger.openDropdownAsButton,
      knowledgeMentionQuery: trigger.mentionQuery,
      knowledgeMentionOpenReason: trigger.mentionOpenReason,
      knowledgeMentionAnchorRef: trigger.mentionAnchorRef,
    },
    isAtModeOpen: trigger.isCharModeOpen,
    cleanupAtText: trigger.cleanupTriggerText,
    handleTextareaFocus: trigger.handleTextareaFocus,
  };
};

export default useKnowledgeMentionHost;
